import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Star, Clock, PoundSterling, ShieldCheck, Siren, Smartphone, Bell } from "lucide-react";
import { SITE } from "@/lib/site";
import { Container, Section, SectionHeader, Reveal, CTAButton } from "@/components/ui-kit";
import { BrowserFrame } from "@/components/mockups/primitives";
import { AIMatch } from "@/components/mockups/intelligence";
import { EscalationLadder } from "@/components/mockups/core";
import { LiveMap, PWASnapshot } from "@/components/mockups/portals";

const SIGNALS = [
  { icon: MapPin, title: "Distance", desc: "Live geo-routing finds the closest qualified supplier in your panel." },
  { icon: Star, title: "Rating", desc: "90-day rolling average of quality, speed and comms on real jobs." },
  { icon: Clock, title: "SLA hit-rate", desc: "How often they acknowledge and complete inside the window." },
  { icon: PoundSterling, title: "Price history", desc: "Average quote vs panel median for the same trade and job type." },
  { icon: ShieldCheck, title: "Compliance", desc: "Hard filter, not a factor: lapsed insurance never reaches the list." },
];

export const AIMatchSection = () => (
  <Section id="ai-match" data-testid="ai-match-section">
    <div className="orb h-[420px] w-[420px] -left-40 top-20 bg-mk-lime/10" aria-hidden />
    <Container className="relative">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5 min-w-0 lg:sticky lg:top-28">
          <SectionHeader eyebrow="Intelligence · Premium differentiator" title={<>Ask Fixlr: <span className="text-mk-lime">"Who's best for this job?"</span></>} desc="AI Supplier Matching ranks your shortlist by distance, compliance, SLA hit-rate and price history — and shows its reasoning. Not a chatbot. A decision co-pilot. Drag the weights and watch the ranking re-order live." />
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {SIGNALS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className="rounded-xl border border-mk-border bg-mk-surface p-4">
                <div className="flex items-center gap-2 text-mk-text font-medium text-sm"><s.icon className="h-4 w-4 text-mk-lime" />{s.title}</div>
                <p className="mt-1.5 text-xs text-mk-dim leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton to="/product/ai-supplier-matching" testId="ai-match-learn-more-btn">How matching works <ArrowRight className="h-4 w-4" /></CTAButton>
            <CTAButton href={SITE.signupUrl} variant="ghost" testId="ai-match-trial-btn">Try it in the sandbox</CTAButton>
          </div>
        </div>
        <Reveal className="lg:col-span-7 min-w-0">
          <BrowserFrame url="app.fixlr.app/jobs/JOB-91AD22C0/assign" className="glow-lime">
            <AIMatch interactive />
          </BrowserFrame>
        </Reveal>
      </div>
    </Container>
  </Section>
);

export const EmergencySection = () => (
  <Section className="bg-mk-surface/40 border-y border-mk-border overflow-hidden" data-testid="emergency-section">
    <div className="orb h-[400px] w-[400px] right-[-120px] top-0 bg-red-500/10" aria-hidden />
    <Container className="relative">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-7 min-w-0 order-2 lg:order-1">
          <BrowserFrame url="app.fixlr.app/emergencies/EMG-4E9D">
            <EscalationLadder />
          </BrowserFrame>
        </Reveal>
        <div className="lg:col-span-5 min-w-0 order-1 lg:order-2">
          <SectionHeader eyebrow="Core Ops · Emergency Response" title="Every minute matters. We track them all." desc="A parallel track for out-of-hours, urgent and safety-critical jobs with hard SLAs, auto-escalation and a red-alert channel in Slack/Teams. This is the reason large accounts pick Fixlr." />
          <ul className="mt-8 space-y-3">
            {[["Tier 1 → 2 → 3 escalation that fires by itself", Siren], ["Push + SMS to whoever is actually on call", Bell], ["Acknowledge / Accept / Resolve with a full audit trail", ShieldCheck], ["Median response and first-time-fix analytics", Clock]].map(([t, I]) => (
              <li key={t} className="flex items-start gap-3 text-sm text-mk-dim"><span className="grid place-items-center h-7 w-7 rounded-lg bg-red-500/15 text-red-300 shrink-0"><I className="h-3.5 w-3.5" /></span><span className="pt-1">{t}</span></li>
            ))}
          </ul>
          <div className="mt-8"><CTAButton to="/product/emergency" variant="ghost" testId="emergency-learn-more-btn">Explore Emergency Response <ArrowRight className="h-4 w-4" /></CTAButton></div>
        </div>
      </div>
    </Container>
  </Section>
);

export const FieldSection = () => (
  <Section data-testid="field-section">
    <Container>
      <SectionHeader align="center" eyebrow="Portals · Your team, on the ground" title="A PWA that turns the van into a command centre." desc="Install on any phone in 10 seconds. A 06:30 briefing, Today's Snapshot, one-tap directions, geo-stamped arrivals — and a live map so the office (and the client) can see the team move." />
      <div className="mt-14 grid lg:grid-cols-12 gap-8 items-center">
        <Reveal className="lg:col-span-4 min-w-0 flex justify-center"><PWASnapshot /></Reveal>
        <Reveal delay={0.1} className="lg:col-span-8 min-w-0">
          <BrowserFrame url="app.fixlr.app/team/map" className="glow-blue">
            <LiveMap />
          </BrowserFrame>
        </Reveal>
      </div>
      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        {[["In-House Team Portal", "in-house", Smartphone, "PWA on iOS + Android, push alerts, tap-through job sheets."], ["Navigation & Geo-tagging", "navigation", MapPin, "Live map, GPS arrivals, shareable client ETA tracker."], ["Client Portal", "client-portal", ShieldCheck, "Requests, quote approvals and Stripe payments — branded."]].map(([t, s, I, d], i) => (
          <Reveal key={s} delay={i * 0.06}>
            <Link to={s === "client-portal" ? "/product/client-portal" : `/platform/portals#${s}`} data-testid={`field-card-${s}`} className="card-dark group p-5 flex items-start gap-4">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-mk-blue/20 text-[#8db6ff] shrink-0"><I className="h-5 w-5" /></span>
              <span><span className="block font-semibold text-mk-text group-hover:text-mk-lime transition-colors">{t}</span><span className="block mt-1 text-sm text-mk-dim">{d}</span></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);
